import { useRef } from 'react';
import { motion, useMotionValue, useTransform, useSpring } from 'framer-motion';
import logo from '../../assets/logo.png';

const DockIcon = ({ mouseX, item }) => {
  const ref = useRef(null);

  const distance = useTransform(mouseX, (val) => {
    const el = ref.current;
    if (!el) return 200;
    const rect = el.getBoundingClientRect();
    return val - rect.x - rect.width / 2;
  });

  const sizeSync = useTransform(distance, [-150, 0, 150], [50, 72, 50]);
  const size = useSpring(sizeSync, { mass: 0.1, stiffness: 150, damping: 12 });

  const iconSizeSync = useTransform(distance, [-150, 0, 150], [20, 28, 20]);
  const iconSize = useSpring(iconSizeSync, { mass: 0.1, stiffness: 150, damping: 12 });

  const Icon = item.icon;
  const isExternal = !item.href.startsWith('#');

  const handleClick = () => {
    if (isExternal) {
      window.location.href = item.href;
    } else {
      const el = document.getElementById(item.href.replace('#', ''));
      if (el) el.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <motion.button
      ref={ref}
      style={{ width: size, height: size }}
      className="dock-tile relative aspect-square rounded-[14px] flex items-center justify-center cursor-pointer transition-colors duration-150 group"
      onClick={handleClick}
      title={item.label}
      whileTap={{ scale: 0.9 }}
    >
      <motion.div style={{ width: iconSize, height: iconSize }}>
        <Icon className="dock-icon w-full h-full transition-colors duration-150" />
      </motion.div>

      {/* Tooltip */}
      <span className="dock-tooltip absolute -top-9 left-1/2 -translate-x-1/2 px-2.5 py-1 rounded-lg text-[11px] whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity duration-150 pointer-events-none">
        {item.label}
      </span>
    </motion.button>
  );
};

const DockDivider = () => (
  <div className="dock-divider-line w-px h-8 mx-1.5 self-center flex-shrink-0" />
);

const Dock = ({ items = [], trailingItems = [] }) => {
  const mouseX = useMotionValue(Infinity);

  return (
    /* Outer wrapper handles fixed positioning + centering (unaffected by framer-motion) */
    <div className="fixed bottom-5 inset-x-0 z-50 hidden md:flex justify-center pointer-events-none">
      <motion.nav
        initial={{ y: 60, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ type: 'spring', bounce: 0.2, duration: 0.6, delay: 0.3 }}
        className="dock-shell pointer-events-auto inline-flex items-center gap-2 pl-4 pr-3.5 py-2.5 rounded-[20px] backdrop-blur-xl"
        onMouseMove={(e) => mouseX.set(e.pageX)}
        onMouseLeave={() => mouseX.set(Infinity)}
        role="navigation"
        aria-label="Main navigation"
      >
        {/* Logo */}
        <a
          href="#home"
          onClick={(e) => {
            e.preventDefault();
            const el = document.getElementById('home');
            if (el) el.scrollIntoView({ behavior: 'smooth' });
          }}
          className="flex items-center mr-1 flex-shrink-0"
          title="NexSav — Home"
        >
          <img src={logo} alt="NexSav" className="dock-logo h-7" />
        </a>

        <DockDivider />

        {/* Main nav icons */}
        {items.map((item, i) => (
          <DockIcon key={i} mouseX={mouseX} item={item} />
        ))}

        {trailingItems.length > 0 && <DockDivider />}

        {/* Trailing icons (Tickets etc.) */}
        {trailingItems.map((item, i) => (
          <DockIcon key={`t-${i}`} mouseX={mouseX} item={item} />
        ))}
      </motion.nav>
    </div>
  );
};

export default Dock;
